import React from 'react'
import classnames from 'classnames/bind'
import { createHighlighterCoreSync } from 'shiki/core'
import { createJavaScriptRegexEngine } from 'shiki/engine/javascript'

// Themes
import githubDarkDefault from '@shikijs/themes/github-dark-default'
import githubLight from '@shikijs/themes/github-light'

// Custom themes
import lightTheme from './themes/light'
import greenTheme from './themes/green'
import purpleTheme from './themes/purple'
import transparentTheme from './themes/transparent'

// Languages
import javascript from '@shikijs/langs/javascript'
import jsx from '@shikijs/langs/jsx'
import typescript from '@shikijs/langs/typescript'
import tsx from '@shikijs/langs/tsx'
import bash from '@shikijs/langs/bash'
import json from '@shikijs/langs/json'
import dart from '@shikijs/langs/dart'
import cssLang from '@shikijs/langs/css'
import html from '@shikijs/langs/html'

import { CopyButton } from '../copyButton/CopyButton'
import styles from './styles.module.scss'

const cx = classnames.bind(styles)

/**
 * Theme mapping
 */
const THEME_MAP = {
  tomorrow: 'github-dark-default',
  transparent: 'transparent',
  light: 'light',
  green: 'green',
  purple: 'purple',
}

const DEFAULT_THEME = 'github-dark-default'
const LANGUAGES_WITHOUT_LINE_NUMBERS = ['bash', 'sh', 'zsh', undefined]

/**
 * Language alias mapping
 */
const LANGUAGE_ALIASES = {
  js: 'javascript',
  ts: 'typescript',
  sh: 'bash',
  zsh: 'bash',
}

const DEFAULT_LANGUAGE = 'javascript'

/**
 * Create synchronous highlighter instance.
 * This runs once at module load time (works on both server and client).
 *
 * Using JavaScript RegExp engine for fully synchronous operation.
 * See: https://shiki.matsu.io/guide/sync-usage#synchronous-usage
 */
const highlighter = createHighlighterCoreSync({
  themes: [
    githubDarkDefault,
    githubLight,
    lightTheme,
    greenTheme,
    purpleTheme,
    transparentTheme,
  ],
  langs: [javascript, jsx, typescript, tsx, bash, json, cssLang, html, dart],
  engine: createJavaScriptRegexEngine(),
})

/**
 * Get the list of loaded language IDs
 */
const loadedLangs = highlighter.getLoadedLanguages()

/**
 * SyntaxHighlighter component using Shiki with synchronous SSR.
 *
 * Highlights code synchronously on both server and client.
 * No hydration mismatch because the same code runs on both sides.
 */
export function SyntaxHighlighter({
  children,
  className,
  customStyle,
  language,
  theme,
}) {
  const code = typeof children === 'string' ? children : String(children || '')
  const showLineNumbers = !LANGUAGES_WITHOUT_LINE_NUMBERS.includes(language)

  const lang = LANGUAGE_ALIASES[language] || language || DEFAULT_LANGUAGE
  const shikiTheme = THEME_MAP[theme] || DEFAULT_THEME
  const effectiveLang = loadedLangs.includes(lang) ? lang : 'text'

  let html = highlighter.codeToHtml(code, {
    lang: effectiveLang,
    theme: shikiTheme,
  })

  // Add line-numbers class to pre tag if needed
  if (showLineNumbers) {
    html = html.replace('<pre class="shiki', '<pre class="shiki line-numbers')
  }

  return (
    <div
      className={cx(styles.wrapper, className)}
      style={customStyle}
    >
      <CopyButton content={code} />
      <div className={cx(styles.content, styles[theme])} dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}
